import { useEffect, type PropsWithChildren, type ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  onClose(): void;
  /** 底部操作区（通常放取消 / 确认按钮） */
  footer?: ReactNode;
  /** 标题使用告警色（删除等危险操作） */
  danger?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * 通用弹窗：深空面板 + 背景模糊遮罩，圆角 14，
 * 标题栏带青色发光竖条（与地图筛选面板 h3::before 同语言）。
 *
 * <Modal open={open} title="新建项目" onClose={close} footer={<>…</>}>…</Modal>
 */
export default function Modal({
  open,
  title,
  onClose,
  footer,
  danger,
  size = 'md',
  className,
  children,
}: PropsWithChildren<ModalProps>) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div
        className={['pm-modal', size === 'sm' ? 'pm-modal--sm' : '', className]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={['pm-modal__title', danger ? 'is-danger' : ''].filter(Boolean).join(' ')}>
          {title}
          <button type="button" className="pm-modal__close" aria-label="关闭" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pm-modal__body">{children}</div>
        {footer && <div className="pm-modal__foot">{footer}</div>}
      </div>
    </div>
  );
}
